import type { AuthResponseDto, LoginInput, SignupInput, UserDto } from "@caloteiros/shared";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { api, onSessionExpired, setAccessToken } from "./api";

interface AuthContextValue {
  user: UserDto | null;
  /** True enquanto a tentativa silenciosa de renovar a sessao nao terminou. */
  loading: boolean;
  login: (input: LoginInput) => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  signup: (input: SignupInput) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: UserDto) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [user, setUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const applyAuth = useCallback((auth: AuthResponseDto) => {
    setAccessToken(auth.accessToken);
    setUser(auth.user);
  }, []);

  // Ao abrir o app, tenta trocar o cookie de refresh por uma sessao nova.
  // Se nao rolar, e so alguem deslogado abrindo a landing.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const auth = await api<AuthResponseDto>("/auth/refresh", { method: "POST", anonymous: true });
        if (active) applyAuth(auth);
      } catch {
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [applyAuth]);

  useEffect(() => {
    onSessionExpired(() => {
      setUser(null);
      queryClient.clear();
    });
  }, [queryClient]);

  const login = useCallback(
    async (input: LoginInput) => {
      applyAuth(await api<AuthResponseDto>("/auth/login", { method: "POST", body: input, anonymous: true }));
    },
    [applyAuth],
  );

  
  const googleLogin = useCallback(
    async (credential: string) => {
      applyAuth(await api<AuthResponseDto>("/auth/google", { method: "POST", body: { credential }, anonymous: true }));
    },
    [applyAuth],
  );

  const signup = useCallback(
    async (input: SignupInput) => {
      applyAuth(await api<AuthResponseDto>("/auth/signup", { method: "POST", body: input, anonymous: true }));
    },
    [applyAuth],
  );

  const logout = useCallback(async () => {
    try {
      await api<void>("/auth/logout", { method: "POST", anonymous: true });
    } catch {
      // Se o servidor nao responder, a sessao local sai do ar do mesmo jeito.
    } finally {
      setAccessToken(null);
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, googleLogin, signup, logout, setUser }),
    [user, loading, login, googleLogin, signup, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth precisa estar dentro de AuthProvider.");
  return context;
}
